import React, { useState, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { PageHeader, Card } from '@/components/common';
import { toast } from 'sonner';
import { UploadCloud, FileText, CheckCircle2, Clock, Eye, FileCheck } from 'lucide-react';

const statusMeta = {
  pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', key: 'docs.status.pending' },
  uploaded: { icon: UploadCloud, color: 'text-cyan-600', bg: 'bg-cyan-50', key: 'docs.status.uploaded' },
  review: { icon: Eye, color: 'text-violet-600', bg: 'bg-violet-50', key: 'docs.status.review' },
  approved: { icon: FileCheck, color: 'text-emerald-600', bg: 'bg-emerald-50', key: 'docs.status.approved' },
};

const Documents = () => {
  const { t, tx, data } = useApp();
  const [requested, setRequested] = useState(data.documents.requested);
  const [recent, setRecent] = useState(data.documents.recent);
  const inputRef = useRef();

  const handleFiles = (files) => {
    const list = Array.from(files);
    if (!list.length) return;
    const added = list.map((f, i) => ({ id: `up-${Date.now()}-${i}`, name: f.name, status: 'uploaded', date: new Date().toISOString().slice(0, 10), size: `${Math.round(f.size / 1024)} KB` }));
    setRecent((r) => [...added, ...r]);
    toast.success(tx({ es: `${list.length} documento(s) subido(s)`, en: `${list.length} document(s) uploaded` }));
  };

  const fulfil = (id) => {
    setRequested((r) => r.map((x) => x.id === id ? { ...x, status: 'uploaded' } : x));
    toast.success(tx({ es: 'Documento marcado como subido', en: 'Document marked as uploaded' }));
  };

  return (
    <div>
      <PageHeader title={t('docs.title')} subtitle={t('docs.subtitle')} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            data-testid="doc-dropzone"
            className="rounded-xl border-2 border-dashed border-zinc-200 bg-white/[0.96] backdrop-blur-xl hover:border-cyan-400 hover:bg-white transition-colors cursor-pointer p-10 flex flex-col items-center justify-center text-center"
          >
            <div className="w-12 h-12 rounded-full bg-cyan-50 flex items-center justify-center mb-3"><UploadCloud className="w-6 h-6 text-cyan-600" /></div>
            <p className="text-sm font-medium text-zinc-700">{t('docs.dropzone')}</p>
            <input ref={inputRef} type="file" multiple hidden onChange={(e) => handleFiles(e.target.files)} data-testid="doc-file-input" />
          </div>

          <Card className="p-6" data-testid="doc-recent">
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400 mb-4">{t('docs.recent')}</p>
            <div className="space-y-2">
              {recent.map((d) => {
                const m = statusMeta[d.status];
                return (
                  <div key={d.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 transition-colors" data-testid={`doc-${d.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-zinc-500" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{tx(d.name)}</p>
                      <p className="text-xs text-zinc-400">{d.date} · {d.size}</p>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${m.bg} ${m.color}`}>{t(m.key)}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <Card className="p-6 h-fit" data-testid="doc-requested">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400 mb-4">{t('docs.requested')}</p>
          <div className="space-y-3">
            {requested.map((d) => {
              const m = statusMeta[d.status];
              return (
                <div key={d.id} className="rounded-lg border border-zinc-200 p-3.5" data-testid={`req-doc-${d.id}`}>
                  <div className="flex items-start gap-2 mb-2">
                    <m.icon className={`w-4 h-4 mt-0.5 ${m.color}`} />
                    <p className="text-sm font-medium text-zinc-800 leading-snug">{tx(d.name)}</p>
                  </div>
                  <p className="text-xs text-zinc-400 mb-2">{t('common.date')}: {d.due}</p>
                  {d.status === 'pending' ? (
                    <button onClick={() => fulfil(d.id)} data-testid={`fulfil-${d.id}`} className="w-full h-8 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold transition-colors">{t('common.upload')}</button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> {t(m.key)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Documents;
